import React from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { PagePath } from '../navigation.enums';
import { AnalysisPageGuard } from '../guards/analysis-page.guard';
import { AssessmentType } from '../../enums/assessment-type';
import { AssessmentPage } from '../../pages/analysis/assessment/assessment.page';
import { FrequencyOfUsePage } from '../../pages/analysis/frequency-of-use/frequency-of-use.page';
import { SelfAssessmentPage } from '../../pages/analysis/rating/rating-technical/self-assessment.page';
import { QuestionnairesPage } from '../../pages/analysis/questionnaires/questionnaires.page';
import { QuestionnairePage } from '../../pages/analysis/questionnaires/questionnaire.page';
import { ChooseYourCareerPathsPage } from '../../pages/analysis/career-path/choose-your-career-paths.page';
import { RateYourPeersSkillsPage } from '../../pages/analysis/rating/rating-technical/rate-your-peers-skills/rate-your-peers-skills.page';
import { PeerSkillsAssessmentPage } from '../../pages/analysis/rating/rating-technical/peer-skills-assessment/peer-skills-assessment.page';
import { PeerSkillRatingPage } from '../../pages/analysis/rating/rating-technical/peer-skill-rating.page';
import { RateYourSkillsPage } from '../../pages/analysis/rating/rating-career-paths/rate-your-skills.page';
import { RatingCareerPathsPage } from '../../pages/analysis/rating/rating-career-paths/rating-career-paths.page';
import { ErrorRedirect } from './routers.helpers';
import { InterimReportDownloadsPage } from '../../pages/analysis/interim-reports-download/interim-reports-download.page';
import { PendingAssessmentsPage } from '../../pages/analysis/assessments/pending-assessments.page';
import { useAppState } from '../../overmind';

export function AnalysisRouter(): JSX.Element {
  const { forcedAssessments, activeAssessmentConfig } = useAppState();

  return (
    <Routes>
      {/* FORCED ASSESSMENTS */}
      <Route
        path={PagePath.analysisPendingAssessments}
        element={
          <AnalysisPageGuard>
            <PendingAssessmentsPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisPendingAssessmentsAssessment}
        element={
          <AnalysisPageGuard assessmentRequired>
            <AssessmentPage />
          </AnalysisPageGuard>
        }
      />

      {/* FORCED ASSESSMENTS - PEER (SELF ONLY) */}
      <Route
        path={PagePath.analysisPendingAssessmentsAssessment + PagePath.analysisFrequencyOfUse}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <FrequencyOfUsePage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisPendingAssessmentsAssessment + PagePath.analysisSelfAssessment}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <SelfAssessmentPage />
          </AnalysisPageGuard>
        }
      />

      {/* FORCED ASSESSMENTS - PEER (PEER ONLY) */}
      <Route
        path={PagePath.analysisPendingAssessmentsAssessment + PagePath.analysisRateYourPeersSkills}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <RateYourPeersSkillsPage />
          </AnalysisPageGuard>
        }
      />

      <Route
        path={PagePath.analysisPendingAssessmentsAssessment + PagePath.analysisPeerSkillsAssessment}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <PeerSkillsAssessmentPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisPendingAssessmentsAssessment + PagePath.analysisPeerSkillsAssessmentRating}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <PeerSkillRatingPage />
          </AnalysisPageGuard>
        }
      />

      {/* FORCED ASSESSMENTS - PEER (SELF + PEER) */}
      <Route
        path={PagePath.analysisPendingAssessmentsAssessment + PagePath.analysisSelfAssessment + PagePath.analysisRateYourPeersSkills}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <RateYourPeersSkillsPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisPendingAssessmentsAssessment + PagePath.analysisSelfAssessment + PagePath.analysisPeerSkillsAssessment}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <PeerSkillsAssessmentPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisPendingAssessmentsAssessment + PagePath.analysisSelfAssessment + PagePath.analysisPeerSkillsAssessmentRating}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <PeerSkillRatingPage />
          </AnalysisPageGuard>
        }
      />

      {/* FORCED ASSESSMENTS - QUESTIONNAIRE */}
      <Route
        path={PagePath.analysisPendingAssessmentsAssessment + PagePath.analysisQuestionnaires}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.Questionnaire}>
            <QuestionnairesPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisPendingAssessmentsAssessment + PagePath.analysisQuestionnaire}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.Questionnaire}>
            <QuestionnairePage />
          </AnalysisPageGuard>
        }
      />

      {/* FORCED ASSESSMENTS - FOP */}
      <Route
        path={PagePath.analysisPendingAssessmentsAssessment + PagePath.analysisChooseYourCareerPaths}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.FieldsOfPractice}>
            <ChooseYourCareerPathsPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisPendingAssessmentsAssessment + PagePath.analysisRateYourSkills}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.FieldsOfPractice}>
            <RateYourSkillsPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisPendingAssessmentsAssessment + PagePath.analysisRateYourSkillsRating}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.FieldsOfPractice}>
            <RatingCareerPathsPage />
          </AnalysisPageGuard>
        }
      />

      {/* NO FORCED ASSESSMENTS */}
      <Route
        path={PagePath.analysisAssessment}
        element={
          <AnalysisPageGuard assessmentRequired>
            <AssessmentPage />
          </AnalysisPageGuard>
        }
      />

      {/* NO FORCED ASSESSMENTS - PEER (SELF ONLY) */}
      <Route
        path={PagePath.analysisAssessment + PagePath.analysisFrequencyOfUse}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <FrequencyOfUsePage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisAssessment + PagePath.analysisSelfAssessment}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <SelfAssessmentPage />
          </AnalysisPageGuard>
        }
      />

      {/* NO FORCED ASSESSMENTS - PEER (PEER ONLY) */}
      <Route
        path={PagePath.analysisAssessment + PagePath.analysisRateYourPeersSkills}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <RateYourPeersSkillsPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisAssessment + PagePath.analysisPeerSkillsAssessment}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <PeerSkillsAssessmentPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisAssessment + PagePath.analysisPeerSkillsAssessmentRating}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <PeerSkillRatingPage />
          </AnalysisPageGuard>
        }
      />

      {/* NO FORCED ASSESSMENTS - PEER (SELF + PEER) */}
      <Route
        path={PagePath.analysisAssessment + PagePath.analysisSelfAssessment + PagePath.analysisRateYourPeersSkills}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <RateYourPeersSkillsPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisAssessment + PagePath.analysisSelfAssessment + PagePath.analysisPeerSkillsAssessment}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <PeerSkillsAssessmentPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisAssessment + PagePath.analysisSelfAssessment + PagePath.analysisPeerSkillsAssessmentRating}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.PeerEndorsement}>
            <PeerSkillRatingPage />
          </AnalysisPageGuard>
        }
      />

      {/* NO FORCED ASSESSMENTS - QUESTIONNAIRE */}
      <Route
        path={PagePath.analysisAssessment + PagePath.analysisQuestionnaires}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.Questionnaire}>
            <QuestionnairesPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisAssessment + PagePath.analysisQuestionnaire}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.Questionnaire}>
            <QuestionnairePage />
          </AnalysisPageGuard>
        }
      />

      {/* NO FORCED ASSESSMENTS - FOP */}
      <Route
        path={PagePath.analysisAssessment + PagePath.analysisChooseYourCareerPaths}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.FieldsOfPractice}>
            <ChooseYourCareerPathsPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisAssessment + PagePath.analysisRateYourSkills}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.FieldsOfPractice}>
            <RateYourSkillsPage />
          </AnalysisPageGuard>
        }
      />
      <Route
        path={PagePath.analysisAssessment + PagePath.analysisRateYourSkillsRating}
        element={
          <AnalysisPageGuard assessmentRequired assessmentTypeRequired={AssessmentType.FieldsOfPractice}>
            <RatingCareerPathsPage />
          </AnalysisPageGuard>
        }
      />

      {/* OTHER */}
      <Route path={PagePath.analysisInterimDownload} element={<InterimReportDownloadsPage />} />

      {/* REDIRECT */}
      <Route
        path={PagePath.root}
        element={
          <Navigate
            to={forcedAssessments.length === 1 ? PagePath.analysisBase + PagePath.analysisAssessment.replace(':assessmentSlug', forcedAssessments[0].assessmentSlug) : forcedAssessments.length > 1 ? PagePath.analysisBase + PagePath.analysisPendingAssessments : !!activeAssessmentConfig?.assessmentSlug ? PagePath.analysisBase + PagePath.analysisAssessment.replace(':assessmentSlug', activeAssessmentConfig.assessmentSlug) : PagePath.yourSkills}
          />
        }
      />
      <Route path={PagePath.rootWildcard} element={<ErrorRedirect />} />
    </Routes>
  );
}
